import { Button, Divider, MuiAlert, Paper, Spin } from '@fox/atoms';
import { Flex, FlexColumnSpaced, FlexRowSpaced, Padded } from '@fox/layout';
import { Form, Formik, FormikHelpers as FormikActions } from 'formik';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useContentContext } from '../../../content-context';
import { CustomFieldValue, SwiftFoxOrganisationDetails, SwiftFoxPersonDetails } from '../../membership-form-template/contracts';
import { CUSTOM_FIELD_COMPONENT_MAP } from '../../membership-form-template/fields';
import { useAddressTypes, useEditRules } from '../../query-hooks/query-implementations';
import { AddressDetails } from '../shared/address';
import { ContactDetails } from '../shared/contacts';
import { CustomFields } from '../shared/custom-fields';
import { HeaderWrapper } from '../shared/profile-style-components';
import { profileDetailsSchema } from '../shared/validation-schema';
import { ProfileComponentProps } from './contracts';

function formatCustomFieldValue(customFieldValue: CustomFieldValue) {
	switch (customFieldValue.type) {
		case 'select':
			return customFieldValue.value
				? JSON.stringify((customFieldValue.value as { id: string; label: string }[]).map(v => v.id))
				: customFieldValue.value;
		case 'link':
		case 'fileupload':
			return customFieldValue.value ? JSON.stringify(customFieldValue.value) : customFieldValue.value;
		default:
			return customFieldValue.value;
	}
}

export function ProfileForm<T extends SwiftFoxPersonDetails | SwiftFoxOrganisationDetails>({
	fetchUrl,
	updateUrl,
	profileComponent,
	useLinkedOrganisationData
}: {
	updateUrl: string;
	fetchUrl: string;
	profileComponent: React.ComponentType<ProfileComponentProps<T>>;
	useLinkedOrganisationData: boolean;
}) {
	const { contentId, requestToken } = useContentContext();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const [error, setError] = useState<string>('');
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [submittedSuccessfully, setSubmittedSuccessFully] = useState<boolean>(false);
	const [initialValues, setInitialValues] = useState<{ initial: T } | undefined>(undefined);
	const {
		loading: addressTypesLoading,
		error: addressTypeError,
		data: addressTypes
	} = useAddressTypes({
		requestToken,
		contentId
	});
	const {
		loading: editRulesLoading,
		error: editRulesError,
		data: editRules
	} = useEditRules({
		requestToken,
		contentId,
		useLinkedOrganisationData
	});

	const ProfileComponent = profileComponent;

	const fetchDetails = async () => {
		try {
			const res = await fetch(`/Umbraco/Api/Membership/${fetchUrl}?contentId=${contentId}`, {
				method: 'GET',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					RequestVerificationToken: requestToken
				}
			});
			if (res.ok) {
				var result = await res.json();
				if (result.success === true) {
					const swiftFoxProfileDetails: T = result.result;
					swiftFoxProfileDetails.contactDetails = swiftFoxProfileDetails.contactDetails.map(c => ({
						...c,
						trackingId: nanoid()
					}));
					swiftFoxProfileDetails.addresses = swiftFoxProfileDetails.addresses.map(a => ({
						...a,
						trackingId: nanoid()
					}));
					setInitialValues({ initial: swiftFoxProfileDetails });
				} else {
					setError(result.message);
				}
			} else {
				const error = await res.text();
				setError(error);
			}
		} catch (e) {
			setError('Something went wrong fetching profile details');
		}
	};
	useEffect(() => {
		fetchDetails();
	}, []);

	const submit = async (values: T, actions: FormikActions<T>) => {
		setSubmittedSuccessFully(false);
		setIsEditing(false);
		//const rcToken = getRcTokenAsync(recaptcha, executeRecaptcha);
		//const body = { ...values, RcToken: await tokenFailable(rcToken), contentId };
		const profileDetailsToUpdate = { ...values };
		profileDetailsToUpdate.addresses = values.addresses
			.map(a => {
				// delete a.trackingId;
				a['type'] = a.addressTypeName;
				// delete a.address.addressTypeName;
				// delete a.address.addressTypeId
				return a;
			})
			.filter(a => !!a);
		profileDetailsToUpdate.contactDetails = values.contactDetails
			.map(c => {
				delete c.trackingId;
				return c;
			})
			.filter(c => !!c);
		//we will only send back the editable fields
		//this way the backend does not need to check for any changes in un-editable fields
		profileDetailsToUpdate.customFields = values.customFields
			.filter(c => {
				return Object.keys(CUSTOM_FIELD_COMPONENT_MAP).includes(c.type);
			})
			.map(c => {
				return {
					...c,
					value: formatCustomFieldValue(c)
				};
			});
		const res = await fetch(`/Umbraco/Api/Membership/${updateUrl}`, {
			method: 'POST',
			body: JSON.stringify({
				contentId,
				...profileDetailsToUpdate
			}),
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				RequestVerificationToken: requestToken
			}
		});
		if (res.ok) {
			const result = await res.json();
			if (result.success) {
				const updatedProfileDetails: T = result.result;
				updatedProfileDetails.contactDetails = updatedProfileDetails.contactDetails.map(c => ({
					...c,
					trackingId: nanoid()
				}));
				updatedProfileDetails.addresses = updatedProfileDetails.addresses.map(a => ({
					...a,
					trackingId: nanoid()
				}));
				setInitialValues({
					initial: {
						...updatedProfileDetails,
						addresses: [...updatedProfileDetails.addresses.map(a => ({ ...a }))],
						contactDetails: [...updatedProfileDetails.contactDetails.map(c => ({ ...c }))],
						customFields: [...updatedProfileDetails.customFields.map(c => ({ ...c }))]
					}
				});
				actions.resetForm({
					values: {
						...updatedProfileDetails,
						addresses: [...updatedProfileDetails.addresses.map(a => ({ ...a }))],
						contactDetails: [...updatedProfileDetails.contactDetails.map(c => ({ ...c }))],
						customFields: [...updatedProfileDetails.customFields.map(c => ({ ...c }))]
					}
				});
				setSubmittedSuccessFully(true);
				actions.setSubmitting(false);
			} else {
				const error = result.message;
				setError(error);
				actions.setSubmitting(false);
			}
		} else {
			const error = await res.text();
			setError(error);
			actions.setSubmitting(false);
		}
	};

	if (error) {
		return <MuiAlert severity="error">{error}</MuiAlert>;
	}

	if (addressTypeError) {
		return <MuiAlert severity="error">{addressTypeError}</MuiAlert>;
	}
	if (editRulesError) {
		return <MuiAlert severity="error">{editRulesError}</MuiAlert>;
	}

	if (!initialValues || addressTypesLoading || editRulesLoading) return <Spin spinning={true} />;

	return (
		<Formik
			initialValues={{
				...initialValues.initial,
				addresses: [...initialValues.initial.addresses.map(a => ({ ...a }))],
				contactDetails: [...initialValues.initial.contactDetails.map(c => ({ ...c }))],
				customFields: [...initialValues.initial.customFields.map(c => ({ ...c }))]
			}}
			validationSchema={profileDetailsSchema}
			onSubmit={submit}
		>
			{({ isSubmitting, touched, errors, setFieldValue, values, resetForm, submitForm }) => {
				return (
					<Form>
						<Spin spinning={isSubmitting}>
							<Paper>
								<Padded>
									<FlexColumnSpaced>
										<HeaderWrapper>
											<FlexRowSpaced
												style={{
													justifyContent: 'space-between',
													width: '100%'
												}}
											>
												{submittedSuccessfully ? (
													<MuiAlert color="success">Details saved successfully</MuiAlert>
												) : (
													<div></div>
												)}
												<Flex>
													<Button
														disabled={isSubmitting}
														sx={!isEditing ? { display: 'none' } : {}}
														variant="tertiary"
														onClick={e => {
															e.stopPropagation();
															setIsEditing(false);
															resetForm({
																values: {
																	...initialValues.initial,
																	addresses: [
																		...initialValues.initial.addresses.map(a => ({
																			...a
																		}))
																	],
																	contactDetails: [
																		...initialValues.initial.contactDetails.map(c => ({
																			...c
																		}))
																	],
																	customFields: [
																		...initialValues.initial.customFields.map(c => ({
																			...c
																		}))
																	]
																}
															});
														}}
													>
														Cancel
													</Button>
													<Button
														sx={{ marginLeft: '1rem' }}
														disabled={isSubmitting}
														startIconName={isEditing ? undefined : 'Edit'}
														variant={isEditing ? 'primary' : 'secondary'}
														onClick={e => {
															e.stopPropagation();
															if (isEditing) {
																submitForm();
															} else {
																setIsEditing(true);
															}
														}}
													>
														{isEditing ? 'Save changes' : 'Edit details'}
													</Button>
												</Flex>
											</FlexRowSpaced>
										</HeaderWrapper>
										<Divider />
										<ProfileComponent
											errors={errors}
											setFieldValue={setFieldValue}
											touched={touched}
											values={values}
											isEditing={isEditing}
											profileFieldRules={editRules.profileFieldRules}
										/>
										<AddressDetails<T>
											errors={errors}
											setFieldValue={setFieldValue}
											touched={touched}
											values={values}
											addressTypes={addressTypes}
											addressTypeRules={editRules.addressTypeRules}
											isEditing={isEditing}
										/>
										<ContactDetails<T>
											errors={errors}
											setFieldValue={setFieldValue}
											touched={touched}
											values={values}
											isEditing={isEditing}
											contactTypeRules={editRules.contactTypeRules}
										/>
										<CustomFields isEditing={isEditing} values={values} setFieldValue={setFieldValue} />
									</FlexColumnSpaced>
								</Padded>
							</Paper>
						</Spin>
					</Form>
				);
			}}
		</Formik>
	);
}
